/**
 * PB-FOUNDATION display brain — one contract for offer row, cart JS, and cart Liquid.
 * Priority: Design Library field label URL → legacy Design File → AI character → upload photo → product fallback.
 * Never use audio URLs as thumbs (harden stay-green).
 */

function fieldTypeLower(field) {
  return String((field && field.type) || '').toLowerCase();
}

function settingsToString(raw) {
  if (raw == null) return '';
  if (typeof raw === 'string') return raw;
  if (typeof raw === 'object') {
    if (typeof raw.settings === 'string') return raw.settings;
    return JSON.stringify(raw);
  }
  return String(raw);
}

function parseAiImageFieldSettings(field) {
  const raw = field && field.settings;
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    return {
      photo_property: raw.photo_property || raw.photoProperty || '',
      character_property: raw.character_property || raw.characterProperty || '',
    };
  }
  const settings = settingsToString(raw);
  if (settings.trim().startsWith('{')) {
    try {
      const parsed = JSON.parse(settings);
      if (parsed && typeof parsed === 'object') {
        return {
          photo_property: parsed.photo_property || parsed.photoProperty || '',
          character_property: parsed.character_property || parsed.characterProperty || '',
        };
      }
    } catch (e) {
      /* fall through */
    }
  }
  const photoMatch = settings.match(/photo_property\s*:\s*([^,\n]+)/i);
  const charMatch = settings.match(/character_property\s*:\s*([^,\n]+)/i);
  return {
    photo_property: photoMatch ? String(photoMatch[1]).trim() : '',
    character_property: charMatch ? String(charMatch[1]).trim() : '',
  };
}

export function getAiImagePhotoKeyFromField(field) {
  if (!field || fieldTypeLower(field).indexOf('ai image') === -1) return null;
  const st = parseAiImageFieldSettings(field);
  return (st.photo_property && String(st.photo_property).trim()) || 'Photo';
}

export function getAiImageCharacterKeyFromField(field) {
  if (!field || fieldTypeLower(field).indexOf('ai image') === -1) return null;
  const st = parseAiImageFieldSettings(field);
  const custom = st.character_property && String(st.character_property).trim();
  if (custom) return custom;
  return (field.label && String(field.label).trim()) || 'Character';
}

/**
 * Tier-1 cart property keys that must survive filterSelectionsForProductFields.
 * @param {Array<{ label?: string, type?: string, settings?: unknown }>} [fieldConfig]
 */
export function collectAiImageAllowedKeys(fieldConfig) {
  const keys = new Set();
  if (!Array.isArray(fieldConfig)) return keys;

  fieldConfig.forEach(function(field) {
    if (!field || fieldTypeLower(field).indexOf('ai image') === -1) return;
    const pk = getAiImagePhotoKeyFromField(field);
    const ck = getAiImageCharacterKeyFromField(field);
    if (pk) keys.add(pk);
    if (ck) keys.add(ck);
    if (field.label) {
      const label = String(field.label).trim();
      keys.add(label);
      keys.add(label + ' - Photo');
      keys.add(label + ' - Character');
    }
  });

  if (keys.size) {
    keys.add('Photo');
    keys.add('Character');
  }
  return keys;
}

function escapeRegExp(str) {
  return String(str || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function readFirstNumberedProp(props, baseKey) {
  if (!props || typeof props !== 'object' || !baseKey) return '';
  const rx = new RegExp('^' + escapeRegExp(baseKey) + '\\s\\((\\d+)\\)\\s*$');
  const collected = [];
  Object.keys(props).forEach(function(key) {
    const match = String(key || '').match(rx);
    if (!match) return;
    const idx = parseInt(match[1], 10);
    const raw = props[key];
    if (raw == null || raw === '') return;
    collected.push({ idx: idx, value: String(raw).trim() });
  });
  collected.sort(function(a, b) { return a.idx - b.idx; });
  return collected.length ? collected[0].value : '';
}

function readPropScalar(props, key) {
  if (!props || !key) return '';
  const val = props[key];
  if (val == null || val === '') return '';
  if (typeof val === 'object') return '';
  return String(val).trim();
}

export function isLikelyAudioUrl(url) {
  if (typeof url !== 'string' || !url.trim()) return false;
  const u = url.trim().toLowerCase();
  if (u.indexOf('/uploads/audio/') !== -1) return true;
  if (u.indexOf('/audio/') !== -1 && u.indexOf('/uploads/photos/') === -1) return true;
  return /\.(webm|mp3|wav|m4a|ogg|aac|opus|flac)(\?|#|$)/i.test(u);
}

function isAudioPropertyKey(key) {
  if (!key) return false;
  const k = String(key).trim().toLowerCase();
  return k.endsWith('(audio)') || k.indexOf(' (audio)') !== -1;
}

export function isLikelyAiPhotoUrl(url) {
  if (typeof url !== 'string' || !url.trim()) return false;
  if (isLikelyAudioUrl(url)) return false;
  const u = url.toLowerCase();
  if (isLikelyAiCharacterUrl(url)) return false;
  if (u.indexOf('cloudinary.com') !== -1) return true;
  if (u.indexOf('/uploads/photos/') !== -1) return true;
  if (u.indexOf('/uploads/') !== -1 && u.indexOf('/generated/') === -1) return true;
  if (u.indexOf('pb-media-worker') !== -1 && u.indexOf('/media/') !== -1) return true;
  return false;
}

export function isLikelyAiCharacterUrl(url) {
  if (typeof url !== 'string' || !url.trim()) return false;
  if (isLikelyAudioUrl(url)) return false;
  const u = url.toLowerCase();
  if (u.indexOf('/uploads/') !== -1) return false;
  return (
    u.indexOf('googleapis.com') !== -1 ||
    u.indexOf('firebasestorage') !== -1 ||
    u.indexOf('/generated/') !== -1 ||
    u.indexOf('workers.dev') !== -1
  );
}

export function isDisplayableImageUrl(val) {
  if (typeof val !== 'string' || !val.trim()) return false;
  if (isLikelyAudioUrl(val)) return false;
  const u = val.trim();
  if (u.indexOf('http://') === 0 || u.indexOf('https://') === 0 || u.indexOf('//') === 0) return true;
  if (u.indexOf('/uploads/') !== -1 || u.indexOf('/generated/') !== -1) return true;
  return false;
}

function normalizeImageUrl(val) {
  const u = String(val || '').trim();
  if (!u) return '';
  if (u.indexOf('//') === 0) return 'https:' + u;
  return u;
}

function collectOrderedKeys(fieldConfig, kind) {
  const keys = [];
  const seen = new Set();
  const add = function(key) {
    const k = String(key || '').trim();
    if (!k || seen.has(k)) return;
    seen.add(k);
    keys.push(k);
  };

  if (kind === 'character') {
    add('Character');
  } else {
    add('Photo');
  }

  if (Array.isArray(fieldConfig)) {
    fieldConfig.forEach(function(field) {
      if (!field || fieldTypeLower(field).indexOf('ai image') === -1) return;
      if (kind === 'character') {
        add(getAiImageCharacterKeyFromField(field));
        if (field.label) {
          add(String(field.label).trim() + ' - Character');
          add(String(field.label).trim());
        }
      } else {
        add(getAiImagePhotoKeyFromField(field));
        if (field.label) add(String(field.label).trim() + ' - Photo');
      }
    });
  }
  return keys;
}

function collectDesignLibraryFieldLabels(fieldConfig) {
  const keys = [];
  const seen = new Set();
  const add = function (key) {
    const k = String(key || '').trim();
    if (!k || seen.has(k)) return;
    seen.add(k);
    keys.push(k);
  };
  if (Array.isArray(fieldConfig)) {
    fieldConfig.forEach(function (field) {
      if (!field || !field.label) return;
      const type = fieldTypeLower(field);
      if (!type.includes('design library') && !type.includes('designlibrary')) return;
      add(field.label);
    });
  }
  if (!keys.length) add('Design');
  return keys;
}

function readFirstUrlForKeys(props, keys) {
  for (let i = 0; i < keys.length; i += 1) {
    const key = keys[i];
    const direct = readPropScalar(props, key);
    if (isDisplayableImageUrl(direct)) return normalizeImageUrl(direct);
    const numbered = readFirstNumberedProp(props, key);
    if (isDisplayableImageUrl(numbered)) return normalizeImageUrl(numbered);
  }
  return '';
}

/**
 * Resolve cart / bundle row thumbnail from line properties (after design library).
 * Prefers generated character over uploaded photo.
 * @param {Record<string, unknown>} props
 * @param {{ fallbackImage?: string, fieldConfig?: Array<{ label?: string, type?: string, settings?: unknown }> }} [options]
 */
export function resolveCartLineDisplayImageUrl(props, options) {
  options = options || {};
  const fallback = options.fallbackImage != null ? String(options.fallbackImage) : '';
  if (!props || typeof props !== 'object') return fallback;

  const fieldConfig = options.fieldConfig || null;
  const designLibraryUrl = readFirstUrlForKeys(props, collectDesignLibraryFieldLabels(fieldConfig));
  if (designLibraryUrl) return designLibraryUrl;

  const designFile = readPropScalar(props, 'Design File');
  if (isDisplayableImageUrl(designFile)) return normalizeImageUrl(designFile);
  const hiddenPreview = readPropScalar(props, '_design_preview_url');
  if (isDisplayableImageUrl(hiddenPreview)) return normalizeImageUrl(hiddenPreview);

  const charUrl = readFirstUrlForKeys(props, collectOrderedKeys(fieldConfig, 'character'));
  if (charUrl) return charUrl;

  const photoUrl = readFirstUrlForKeys(props, collectOrderedKeys(fieldConfig, 'photo'));
  if (photoUrl) return photoUrl;

  let photoCandidate = '';
  let characterCandidate = '';
  Object.keys(props).forEach(function(key) {
    if (!key || key.charAt(0) === '_') return;
    if (key === 'Design Source') return;
    if (isAudioPropertyKey(key)) return;
    const val = readPropScalar(props, key);
    if (!isDisplayableImageUrl(val)) return;
    const lk = key.toLowerCase();
    if (!characterCandidate && (lk.indexOf('character') !== -1 || isLikelyAiCharacterUrl(val))) {
      characterCandidate = normalizeImageUrl(val);
      return;
    }
    if (!photoCandidate && (lk.indexOf('photo') !== -1 || isLikelyAiPhotoUrl(val))) {
      photoCandidate = normalizeImageUrl(val);
    }
  });
  if (characterCandidate) return characterCandidate;
  if (photoCandidate) return photoCandidate;

  for (let i = 0; i < Object.keys(props).length; i += 1) {
    const key = Object.keys(props)[i];
    if (!key || key.charAt(0) === '_') continue;
    if (key === 'Design Source') continue;
    if (isAudioPropertyKey(key)) continue;
    const val = readPropScalar(props, key);
    if (isDisplayableImageUrl(val)) return normalizeImageUrl(val);
  }

  return fallback;
}

/** Canonical attached-media display resolver (alias for cart / offer / bundle row). */
export const resolveAttachedDisplayImageUrl = resolveCartLineDisplayImageUrl;

/** Presentation contract — paired with URL brain (PB-CART-THUMB-DISPLAY). */
export const ATTACHED_MEDIA_THUMB_IMG_CLASS = 'pb-attached-media-thumb';
export const ATTACHED_MEDIA_THUMB_FRAME_CLASS = 'pb-attached-media-thumb__frame';
/** Default timeline / bundle row thumb (px). */
export const ATTACHED_MEDIA_THUMB_SIZE_TIMELINE = 60;
/** Nested add-on line item thumb (px). */
export const ATTACHED_MEDIA_THUMB_SIZE_ADDON_ROW = 40;

/**
 * True when resolved URL is customer attached media (not bare product fallback).
 * @param {string} resolvedUrl
 * @param {string} [fallbackUrl]
 */
export function isAttachedMediaDisplayUrl(resolvedUrl, fallbackUrl) {
  const resolved = String(resolvedUrl || '').trim();
  const fallback = String(fallbackUrl || '').trim();
  if (!resolved || !isDisplayableImageUrl(resolved)) return false;
  if (fallback && resolved === fallback) return false;
  return true;
}

/**
 * Builder timeline thumb markup — frame + center-crop img for attached media; plain pb-t-img for product fallback.
 * @param {{ src: string, fallbackUrl?: string, esc?: (v: unknown) => string, width?: number, height?: number }} options
 */
export function buildAttachedMediaThumbHtml(options) {
  options = options || {};
  const src = String(options.src || '').trim();
  const fallbackUrl = options.fallbackUrl != null ? String(options.fallbackUrl) : '';
  const esc = typeof options.esc === 'function' ? options.esc : function(v) { return String(v); };
  const width = options.width != null ? options.width : ATTACHED_MEDIA_THUMB_SIZE_TIMELINE;
  const height = options.height != null ? options.height : width;
  const frameStyle = ' style="--pb-attached-thumb-size:' + width + 'px"';

  if (!isAttachedMediaDisplayUrl(src, fallbackUrl)) {
    return (
      '<img src="' + esc(src) + '" class="pb-t-img" width="' + width + '" height="' + height +
      '" loading="lazy" alt="">'
    );
  }

  let onerror = '';
  if (fallbackUrl) {
    onerror =
      ' onerror="if(this.dataset.pbFallbackApplied){return;}this.dataset.pbFallbackApplied=\'1\';' +
      'this.src=\'' + esc(fallbackUrl) + '\';' +
      'this.className=\'pb-t-img\';' +
      'var f=this.parentElement;' +
      'if(f&&f.classList&&f.classList.contains(\'' + ATTACHED_MEDIA_THUMB_FRAME_CLASS + '\')){' +
      'f.replaceWith(this);}"';
  }

  return (
    '<span class="' + ATTACHED_MEDIA_THUMB_FRAME_CLASS + '"' + frameStyle + '>' +
    '<img src="' + esc(src) + '" class="' + ATTACHED_MEDIA_THUMB_IMG_CLASS + ' pb-t-img" width="' +
    width + '" height="' + height + '" loading="lazy" alt=""' + onerror + '>' +
    '</span>'
  );
}

const api = {
  getAiImagePhotoKeyFromField: getAiImagePhotoKeyFromField,
  getAiImageCharacterKeyFromField: getAiImageCharacterKeyFromField,
  collectAiImageAllowedKeys: collectAiImageAllowedKeys,
  isLikelyAiPhotoUrl: isLikelyAiPhotoUrl,
  isLikelyAiCharacterUrl: isLikelyAiCharacterUrl,
  isLikelyAudioUrl: isLikelyAudioUrl,
  isDisplayableImageUrl: isDisplayableImageUrl,
  resolveCartLineDisplayImageUrl: resolveCartLineDisplayImageUrl,
  resolveAttachedDisplayImageUrl: resolveAttachedDisplayImageUrl,
  ATTACHED_MEDIA_THUMB_IMG_CLASS: ATTACHED_MEDIA_THUMB_IMG_CLASS,
  ATTACHED_MEDIA_THUMB_FRAME_CLASS: ATTACHED_MEDIA_THUMB_FRAME_CLASS,
  ATTACHED_MEDIA_THUMB_SIZE_TIMELINE: ATTACHED_MEDIA_THUMB_SIZE_TIMELINE,
  ATTACHED_MEDIA_THUMB_SIZE_ADDON_ROW: ATTACHED_MEDIA_THUMB_SIZE_ADDON_ROW,
  isAttachedMediaDisplayUrl: isAttachedMediaDisplayUrl,
  buildAttachedMediaThumbHtml: buildAttachedMediaThumbHtml,
};

if (typeof globalThis !== 'undefined') {
  globalThis.PBCartDisplayImage = api;
}
